// Client-scope enforcement for Lenses piece 3.
//
// When a client-role user connects, the JWT's `scope: "client-grants"` claim
// tells the daemon to look them up in the scoped section of the auth-server
// allowlist. That lookup returns this `ClientScope` object — the shape of
// "what this client is allowed to do." The daemon's message dispatch then
// runs every inbound message through `evaluateClientScopeMessage` before
// touching any real handler. Default-deny.

import type { SessionInboundMessage } from "../../shared/messages.js";
import type { Session } from "../session.js";

/** In-memory scope for a client-scoped connection. */
export interface ClientScope {
  kind: "client-grants";
  userId: string;
  grants: ReadonlyArray<{
    feature: string;
    projectRef: string;
    config: Record<string, unknown>;
  }>;
}

/** Decision returned by `evaluateClientScopeMessage`. */
export type ClientScopeDecision = { allow: true } | { allow: false; code: string; reason: string };

/**
 * Allow-matrix. This is the whole point of client-scope: only messages that
 * appear here are allowed. Everything else → default-deny.
 *
 * Post-pivot (2026-07-02) — programmatic renders, no agent spawning:
 *   - `lenses_list_request` — cwd must equal a grant's projectRef. The handler
 *     itself is responsible for filtering the response to `slug ∈ grant.config.lenses`.
 *   - `lenses_render_request` — cwd + lensSlug must be within grants. Full
 *     validation happens inside the render handler (subject sanitizer,
 *     variable schema check).
 *   - `lenses_renders_list_request` — cwd + lensSlug must be within grants;
 *     replays that lens's saved render history (read-only).
 *   - `lenses_upload_request` — cwd + lensSlug must be within grants; saves
 *     a validated content image to `<lens-dir>/uploads/` (composite lenses).
 *
 * Everything else — `create_agent_request`, `send_agent_message_request`,
 * `stack_*`, `todo_*`, `share_session_request`, terminals, file browser, etc.
 * — is hard-denied with code `client_scope_denied`.
 */
export function evaluateClientScopeMessage(
  message: SessionInboundMessage,
  scope: ClientScope,
  _session: Session,
): ClientScopeDecision {
  switch (message.type) {
    case "lenses_list_request": {
      const cwd = (message as { cwd?: string }).cwd;
      if (typeof cwd !== "string") {
        return { allow: false, code: "client_scope_denied", reason: "missing cwd" };
      }
      const grant = scope.grants.find((g) => g.projectRef === cwd);
      if (!grant) {
        return {
          allow: false,
          code: "client_scope_denied",
          reason: "cwd not in scope",
        };
      }
      return { allow: true };
    }
    case "lenses_render_request":
    case "lenses_renders_list_request":
    case "lenses_upload_request": {
      const cwd = (message as { cwd?: string }).cwd;
      const lensSlug = (message as { lensSlug?: string }).lensSlug;
      if (typeof cwd !== "string" || typeof lensSlug !== "string") {
        return { allow: false, code: "client_scope_denied", reason: "missing cwd/lensSlug" };
      }
      const grant = scope.grants.find((g) => g.projectRef === cwd);
      if (!grant) {
        return {
          allow: false,
          code: "client_scope_denied",
          reason: "cwd not in scope",
        };
      }
      const lenses = Array.isArray((grant.config as { lenses?: unknown }).lenses)
        ? ((grant.config as { lenses: unknown[] }).lenses as unknown[]).filter(
            (v): v is string => typeof v === "string",
          )
        : [];
      if (!lenses.includes(lensSlug)) {
        return {
          allow: false,
          code: "client_scope_denied",
          reason: "lens not in grant",
        };
      }
      return { allow: true };
    }
    default:
      return {
        allow: false,
        code: "client_scope_denied",
        reason: `message type ${message.type} not permitted for client scope`,
      };
  }
}

/**
 * Deterministic v1 subject sanitizer. No LLM pass — that's a v1.1 upgrade.
 *
 * Rules:
 *   - Reject if contains \n or \r (single-line only).
 *   - Strip control chars, backticks, angle brackets.
 *   - Length cap 100 chars (post-strip).
 *   - Reject if empty after strip.
 */
export interface SanitizeResult {
  ok: boolean;
  value?: string;
  error?: string;
}

export function sanitizeSubject(input: unknown): SanitizeResult {
  if (typeof input !== "string") return { ok: false, error: "subject not a string" };
  if (input.includes("\n") || input.includes("\r")) {
    return { ok: false, error: "subject must be single-line" };
  }
  // Strip control chars, backticks, angle brackets.
  // biome-ignore lint/suspicious/noControlCharactersInRegex: intentional
  const stripped = input.replace(/[ -`<>]/g, "").trim();
  if (stripped.length === 0) return { ok: false, error: "subject empty after sanitize" };
  if (stripped.length > 100) {
    return { ok: true, value: stripped.slice(0, 100) };
  }
  return { ok: true, value: stripped };
}

/**
 * Validate `variables` against a lens's declared schema. The lens schema
 * (parsed from frontmatter by `scanner.ts`) declares each variable with a
 * type/options. Only the shape we know how to enforce today: `string`,
 * `enum`, `boolean`. Unknown types → strict reject.
 *
 * Missing required variable → error. Extra keys → dropped silently (allowed
 * for forward-compat but stripped so nothing sneaks into the prompt).
 */
export interface VariableSchemaEntry {
  key: string;
  type?: string;
  required?: boolean;
  options?: readonly string[];
  default?: string;
}

export interface ValidateVariablesResult {
  ok: boolean;
  values?: Record<string, string>;
  error?: string;
}

export function validateVariables(
  schema: ReadonlyArray<VariableSchemaEntry> | null | undefined,
  raw: Record<string, unknown> | null | undefined,
): ValidateVariablesResult {
  const schemaList = schema ?? [];
  const input = raw ?? {};
  const out: Record<string, string> = {};
  for (const entry of schemaList) {
    const v = input[entry.key];
    if (v === undefined || v === null || v === "") {
      if (entry.default !== undefined) {
        out[entry.key] = entry.default;
        continue;
      }
      if (entry.required) {
        return { ok: false, error: `missing required variable: ${entry.key}` };
      }
      continue;
    }
    if (typeof v === "boolean") {
      out[entry.key] = v ? "true" : "false";
      continue;
    }
    if (typeof v !== "string") {
      return { ok: false, error: `variable ${entry.key} must be a string` };
    }
    if (entry.options && entry.options.length > 0 && !entry.options.includes(v)) {
      return { ok: false, error: `variable ${entry.key} not in allowed options` };
    }
    // Strip control chars from string values (defensive).
    // biome-ignore lint/suspicious/noControlCharactersInRegex: intentional
    out[entry.key] = v.replace(/[ -]/g, "").trim();
  }
  return { ok: true, values: out };
}
